import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Paper,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { API_BASE_URL } from "../../config";
import { SidebarContext } from "../../components/layout/DashboardLayout";
import IntakeSuccessToast from "../../components/intake/IntakeSuccessToast";
import { resolveGate0 } from "../../utils/gate0Storage";
import { createItem, createSection } from "../framework/frameworkUtils";
import { getPresetFramework } from "../framework/presets";
import { FR_FIXED_SECTIONS, isFixedFrSection } from "../framework/frElicitationSections";

const DEPTH_OPTIONS = [
  { key: "lite", label: "Lightweight", hint: "4 sections — pilots, internal tools" },
  { key: "standard", label: "Standard", hint: "6 sections — balanced coverage" },
  { key: "enterprise", label: "Enterprise", hint: "7 sections — full depth" },
  { key: "regulated", label: "Regulated", hint: "9 sections — audit & evidence" },
  { key: "healthcare", label: "Healthcare", hint: "8 sections — clinical domain" },
  { key: "defaults", label: "Default Sections", hint: "Use the standard Gate 0 sections" },
  { key: "manual", label: "Set my own", hint: "Choose section count (1–20)" },
];

const CAPABILITIES = [
  "Conversational chat",
  "Retrieval / RAG",
  "Agentic / tool use",
  "Voice",
  "Document processing",
  "Summarisation",
  "Classification / routing",
  "Recommendation",
];

const requiredMarkSx = { color: "#DE350B", ml: 0.25 };

const PAGE_FONT = "Lato, sans-serif";
const BLUE = "#005BF0";
const TEXT = "#383838";
const MUTED = "#707070";
const BTN_SHADOW = "0px 1px 2px 0px #0000000A";
const BTN_INSET = "inset 0px 1px 4px 0px rgba(0, 0, 0, 0.5)";

/** Same outlined Back treatment used on Configuration, chat, and Gate 0. */
const ghostBtnSx = {
  textTransform: "none",
  fontFamily: PAGE_FONT,
  fontWeight: 700,
  fontSize: 15,
  lineHeight: "24px",
  letterSpacing: 0,
  minWidth: 0,
  width: "fit-content",
  height: 40,
  minHeight: 40,
  px: "16px",
  py: "8px",
  gap: "4px",
  boxSizing: "border-box",
  borderRadius: "4px",
  border: `1px solid ${BLUE}`,
  bgcolor: "#FFFFFF",
  backgroundColor: "#FFFFFF",
  backgroundImage: "none",
  color: BLUE,
  boxShadow: BTN_SHADOW,
  whiteSpace: "nowrap",
  "&:hover:not(.Mui-disabled):not(:focus):not(:focus-visible):not(.Mui-focusVisible):not(:active)": {
    bgcolor: "#F0F6FF",
    backgroundColor: "#F0F6FF",
    backgroundImage: "none",
    border: `1px solid ${BLUE}`,
    color: BLUE,
    boxShadow: BTN_SHADOW,
  },
  "&:focus:not(:active), &:focus-visible:not(:active), &.Mui-focusVisible:not(:active)": {
    outline: "none",
    bgcolor: "#FFFFFF !important",
    backgroundColor: "#FFFFFF !important",
    backgroundImage: "none !important",
    border: `2px solid ${BLUE}`,
    color: BLUE,
    boxShadow: BTN_SHADOW,
  },
  "&:hover:focus:not(:active), &:hover:focus-visible:not(:active), &:hover.Mui-focusVisible:not(:active)": {
    bgcolor: "#FFFFFF !important",
    backgroundColor: "#FFFFFF !important",
    backgroundImage: "none !important",
    border: `2px solid ${BLUE}`,
    color: BLUE,
  },
  "&:active:not(.Mui-disabled)": {
    bgcolor: "#FFFFFF !important",
    backgroundColor: "#FFFFFF !important",
    backgroundImage: "none !important",
    border: `1px solid ${BLUE}`,
    color: BLUE,
    boxShadow: BTN_INSET,
  },
  "& .MuiTouchRipple-root": { display: "none" },
  "&.Mui-disabled": {
    bgcolor: "#F5F5F5",
    backgroundColor: "#F5F5F5",
    backgroundImage: "none",
    border: "1px solid #E6E6E6",
    color: "#BBBBBB",
    boxShadow: "0px 1px 2px 0px rgba(0, 0, 0, 0.08)",
    cursor: "not-allowed",
  },
};

/** Same solid primary treatment as View Summary / Configuration Next. */
const primaryBtnSx = {
  textTransform: "none",
  fontFamily: PAGE_FONT,
  fontWeight: 700,
  fontSize: 15,
  lineHeight: "24px",
  letterSpacing: 0,
  minWidth: 0,
  width: "fit-content",
  height: 40,
  minHeight: 40,
  px: "16px",
  py: "8px",
  gap: "4px",
  boxSizing: "border-box",
  borderRadius: "4px",
  bgcolor: BLUE,
  backgroundColor: BLUE,
  backgroundImage: "none",
  color: "#FFFFFF",
  border: "1px solid transparent",
  boxShadow: "none",
  whiteSpace: "nowrap",
  "&:hover:not(.Mui-disabled):not(:focus):not(:focus-visible):not(.Mui-focusVisible):not(:active)": {
    bgcolor: "#0046B8",
    backgroundColor: "#0046B8",
    backgroundImage: "none",
    color: "#FFFFFF",
    border: "1px solid transparent",
  },
  "&:focus:not(:active), &:focus-visible:not(:active), &.Mui-focusVisible:not(:active)": {
    outline: "none",
    bgcolor: `${BLUE} !important`,
    backgroundColor: `${BLUE} !important`,
    backgroundImage: "none !important",
    color: "#FFFFFF",
    border: `2px solid ${BLUE}`,
    boxShadow: "0 0 0 2px #FFFFFF, 0 0 0 4px #005BF0",
  },
  "&:hover:focus:not(:active), &:hover:focus-visible:not(:active), &:hover.Mui-focusVisible:not(:active)": {
    bgcolor: `${BLUE} !important`,
    backgroundColor: `${BLUE} !important`,
    backgroundImage: "none !important",
    color: "#FFFFFF",
    border: `2px solid ${BLUE}`,
    boxShadow: "0 0 0 2px #FFFFFF, 0 0 0 4px #005BF0",
  },
  "&:active:not(.Mui-disabled)": {
    bgcolor: "#0046B8 !important",
    backgroundColor: "#0046B8 !important",
    backgroundImage: "none !important",
    color: "#FFFFFF",
    border: "1px solid transparent",
    boxShadow: BTN_INSET,
  },
  "& .MuiTouchRipple-root": { display: "none" },
  "&.Mui-disabled": {
    bgcolor: BLUE,
    color: "#FFFFFF",
    opacity: 0.6,
  },
};

const pageTypeSx = {
  fontFamily: PAGE_FONT,
  color: TEXT,
  "& .MuiTypography-root, & .MuiButton-root, & .MuiInputBase-root, & .MuiInputLabel-root, & .MuiFormControlLabel-label, & .MuiChip-label, & .MuiDialogTitle-root": {
    fontFamily: PAGE_FONT,
  },
  "& .MuiTypography-h6": {
    fontFamily: PAGE_FONT,
    fontWeight: 700,
    fontSize: 18,
    lineHeight: "24px",
    color: TEXT,
  },
  "& .MuiInputBase-input": {
    fontFamily: PAGE_FONT,
    fontSize: 16,
  },
};

const dialogPaperSx = {
  fontFamily: PAGE_FONT,
  "& .MuiTypography-root, & .MuiDialogTitle-root, & .MuiInputBase-root, & .MuiInputLabel-root, & .MuiFormControlLabel-label": {
    fontFamily: PAGE_FONT,
  },
  "& .MuiDialogTitle-root": {
    fontWeight: 700,
    fontSize: 18,
    color: TEXT,
  },
  "& .MuiInputBase-input": {
    fontFamily: PAGE_FONT,
    fontSize: 16,
  },
};

const pageTitleSx = {
  fontFamily: PAGE_FONT,
  fontWeight: 700,
  fontSize: 22,
  lineHeight: "28px",
  color: TEXT,
};

const metaSx = {
  fontFamily: PAGE_FONT,
  fontSize: 14,
  lineHeight: "21px",
  color: MUTED,
};

const stepLabelSx = {
  fontFamily: PAGE_FONT,
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  color: MUTED,
  mb: 1,
};

const FR_FIXED_SECTION_NAMES = new Set(FR_FIXED_SECTIONS.map((section) => section.name));

function isLockedTemplateSection(section) {
  if (isFixedFrSection(section)) return true;
  return FR_FIXED_SECTION_NAMES.has(String(section?.name || "").trim());
}

/** Fixed sections stay in the saved draft, but Customize only edits the rest. */
function partitionFramework(framework) {
  const base = ensureFramework(framework);
  const editable = [];
  const fixed = [];
  (base.sections || []).forEach((section) => {
    if (isFixedFrSection(section)) fixed.push(section);
    else editable.push(section);
  });
  return { framework: { ...base, sections: editable }, fixed };
}

function attachFixedSections(framework, fixed) {
  const base = ensureFramework(framework);
  const editable = (base.sections || []).filter((section) => !isFixedFrSection(section));
  return { ...base, sections: [...editable, ...(fixed || [])] };
}

const lockChipSx = {
  display: "inline-flex",
  alignItems: "center",
  gap: "3px",
  fontFamily: PAGE_FONT,
  fontWeight: 700,
  fontSize: 11,
  lineHeight: "16px",
  letterSpacing: "0.3px",
  color: "#707070",
  bgcolor: "#F0F0F0",
  border: "1px solid #E0E0E0",
  borderRadius: "4px",
  px: "6px",
  py: "1px",
  cursor: "default",
  textTransform: "uppercase",
};

const FIXED_SECTION_TOOLTIP = "Fixed by ValidAIte to capture functional requirements — cannot be edited or removed. You'll answer these in the chat.";

const actionIconSx = { fontSize: 20, width: 20, height: 20, display: "block" };
const actionIconButtonSx = { width: 32, height: 32, p: 0, color: MUTED };

const bodySx = {
  fontFamily: PAGE_FONT,
  fontSize: 14,
  lineHeight: "21px",
  color: MUTED,
};

const PERMISSION_OPTIONS = [
  { value: "answer_only", label: "Answer only", desc: "Structure locked — intake chat only" },
  { value: "answer_and_comment", label: "Answer and comment", desc: "Structure locked — can flag questions (comments coming soon)" },
  { value: "answer_and_edit", label: "Answer and edit", desc: "EAO can add or reword sections and questions" },
];

function isRequiredQuestion(q) {
  return q?.required !== false;
}

function hasSelectedCapability(capabilities) {
  return Array.isArray(capabilities) && capabilities.some((cap) => String(cap || "").trim());
}

const CAPABILITY_REQUIRED_MESSAGE = "Please select at least one capability to generate sections.";

function ensureFramework(base) {
  if (base && Array.isArray(base.sections)) return base;
  return {
    name: "Tenant onboarding template",
    version: 2,
    mandatory_defaults_version: 3,
    passing_mode: "weighted",
    default_threshold: 85,
    generated: false,
    sections: [],
  };
}

function isUsableApplicationFramework(framework) {
  const sections = framework?.sections;
  if (!Array.isArray(sections) || sections.length === 0) return false;
  return sections.some((section) => Array.isArray(section?.items) && section.items.length > 0);
}

function cloneFramework(framework) {
  return JSON.parse(JSON.stringify(framework));
}

function countLabel(count, singular, plural) {
  const n = Number(count) || 0;
  return `${n} ${n === 1 ? singular : plural}`;
}

function ApplicationTemplateLibrary({
  templates,
  error,
  applyingId,
  preview,
  onBack,
  onView,
  onClosePreview,
  onUse,
  onScratch,
}) {
  return (
    <Box sx={{ bgcolor: "#F5F5F5", minHeight: "100vh", pb: 6, ...pageTypeSx }}>
      <Box sx={{ bgcolor: "#fff", borderBottom: "1px solid #E6E6E6", px: 3, py: 1.5, display: "flex", alignItems: "center", gap: 2 }}>
        <IconButton size="small" onClick={onBack} sx={{ color: TEXT }}>
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography sx={pageTitleSx}>
            Application onboarding template
          </Typography>
          <Typography sx={metaSx}>
            Choose a starting skeleton
          </Typography>
        </Box>
      </Box>

      <Box sx={{ maxWidth: 840, mx: "auto", px: 3, py: 4 }}>
        <Typography sx={stepLabelSx}>
          Existing Templates
        </Typography>
        <Typography variant="h6" sx={{ mb: 0.5 }}>
          Start from a template in this organisation
        </Typography>
        <Typography sx={{ ...bodySx, mb: 3 }}>
          Published templates from other applications in this tenant can be copied as a starting point.
          Editing the copy does not change the original application.
        </Typography>

        {templates.length === 0 && !error && (
          <Paper variant="outlined" sx={{ p: 3, mb: 2, borderRadius: 2 }}>
            <Typography sx={bodySx}>
              No previous customized templates are available for this tenant.
            </Typography>
          </Paper>
        )}

        {templates.map((item) => (
          <Paper
            key={item.project_id}
            variant="outlined"
            sx={{
              p: 2,
              mb: 1.5,
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <Box sx={{ flex: 1, minWidth: 200 }}>
              <Typography sx={{ fontFamily: PAGE_FONT, fontWeight: 700, fontSize: 15, lineHeight: "24px", color: TEXT }}>
                {item.project_name}
              </Typography>
              <Typography sx={{ ...metaSx, mt: 0.25 }}>
                {countLabel(item.section_count, "section", "sections")}
                {" • "}
                {countLabel(item.question_count, "question", "questions")}
              </Typography>
            </Box>
            <Button
              variant="outlined"
              disableElevation
              disableRipple
              disableFocusRipple
              onClick={() => onView(item)}
              disabled={Boolean(applyingId)}
              sx={ghostBtnSx}
            >
              View Template
            </Button>
            <Button
              variant="contained"
              disableElevation
              disableRipple
              disableFocusRipple
              onClick={() => onUse(item)}
              disabled={Boolean(applyingId)}
              startIcon={applyingId === item.project_id ? <CircularProgress size={16} sx={{ color: "#FFFFFF" }} /> : null}
              sx={primaryBtnSx}
            >
              {applyingId === item.project_id ? "Copying…" : "Use Template"}
            </Button>
          </Paper>
        ))}

        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
          <Button
            variant="outlined"
            disableElevation
            disableRipple
            disableFocusRipple
            onClick={onScratch}
            disabled={Boolean(applyingId)}
            sx={ghostBtnSx}
          >
            Start from Scratch
          </Button>
        </Box>
      </Box>

      <Dialog open={Boolean(preview)} onClose={onClosePreview} fullWidth maxWidth="md" PaperProps={{ sx: dialogPaperSx }}>
        <DialogTitle>
          {preview?.project_name || "Template"}
          <Typography sx={{ ...metaSx, mt: 0.5 }}>
            Read-only preview
            {preview
              ? ` · ${countLabel(preview.section_count, "section", "sections")} • ${countLabel(preview.question_count, "question", "questions")}`
              : ""}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {(preview?.framework?.sections || []).map((section, index) => (
            <Box key={section.id || index} sx={{ mb: 2.5 }}>
              <Typography sx={{ fontFamily: PAGE_FONT, fontWeight: 700, fontSize: 15, lineHeight: "24px", color: TEXT }}>
                {section.name || `Section ${index + 1}`}
              </Typography>
              {section.purpose && (
                <Typography sx={{ ...metaSx, mt: 0.25 }}>
                  {section.purpose}
                </Typography>
              )}
              <Typography sx={{ ...metaSx, mt: 0.5 }}>
                {section.required === false ? "section optional" : "section required"}
                {" · "}
                {countLabel(section.items?.length || 0, "question", "questions")}
              </Typography>
              {(section.items || []).length === 0 && (
                <Typography sx={{ ...metaSx, fontStyle: "italic", mt: 1 }}>
                  No questions.
                </Typography>
              )}
              {(section.items || []).map((question, qi) => (
                <Box
                  key={question.id || qi}
                  sx={{
                    mt: 1,
                    py: 1,
                    px: 1.5,
                    borderRadius: 1,
                    bgcolor: "#f9fafb",
                    border: "1px solid #eef1f4",
                  }}
                >
                  <Typography sx={{ fontFamily: PAGE_FONT, fontSize: 14, lineHeight: "21px", color: TEXT }}>
                    {question.text}
                    {isRequiredQuestion(question) && (
                      <Box component="span" sx={requiredMarkSx}>*</Box>
                    )}
                  </Typography>
                  <Typography sx={{ ...metaSx, mt: 0.25 }}>
                    {question.required === false ? "optional" : "required"}
                  </Typography>
                </Box>
              ))}
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            disableElevation
            disableRipple
            disableFocusRipple
            onClick={onClosePreview}
            sx={ghostBtnSx}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function SimilarityPlace({
  severity,
  severityLabel,
  text,
  location,
  canGo,
  goLabel,
  onGo,
  sx,
}) {
  const blocked = severity === "block"
    || severity === "error"
    || severity === "hard_duplicate"
    || severity === "duplicate";
  return (
    <Box sx={sx}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
        <Chip
          size="small"
          label={severityLabel}
          color={blocked ? "error" : "warning"}
          sx={{ fontFamily: PAGE_FONT, fontWeight: 700, fontSize: 12, height: 22 }}
        />
        <Typography sx={{ ...metaSx, color: TEXT }}>{location}</Typography>
      </Box>
      <Typography sx={bodySx}>“{text}”</Typography>
      {canGo && (
        <Button
          variant="text"
          disableRipple
          onClick={onGo}
          sx={{
            mt: 0.5,
            p: 0,
            minWidth: 0,
            height: "auto",
            fontFamily: PAGE_FONT,
            fontWeight: 700,
            fontSize: 14,
            color: BLUE,
            textTransform: "none",
            "&:hover": { bgcolor: "transparent", textDecoration: "underline" },
          }}
        >
          {goLabel}
        </Button>
      )}
    </Box>
  );
}

function SectionBlock({
  section,
  index,
  open,
  onToggle,
  onEditSection,
  onDeleteSection,
  onEditQuestion,
  onDeleteQuestion,
  onAddQuestion,
}) {
  const qCount = section.items?.length || 0;
  const locked = isLockedTemplateSection(section);
  return (
    <Paper id={`template-section-${index}`} variant="outlined" sx={{ mb: 1, borderRadius: 2, overflow: "hidden" }}>
      <Box
        sx={{
          px: 2,
          py: 1.5,
          display: "flex",
          alignItems: "flex-start",
          gap: 1,
          "&:hover": { bgcolor: "#fafcfd" },
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0, cursor: "pointer" }} onClick={onToggle}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <Typography sx={{ fontFamily: PAGE_FONT, fontWeight: 700, fontSize: 15, lineHeight: "24px", color: TEXT }}>
              {section.name}
            </Typography>
            {locked && (
              <Tooltip title={FIXED_SECTION_TOOLTIP} arrow placement="top">
                <Box component="span" sx={lockChipSx}>
                  <LockOutlinedIcon sx={{ fontSize: 12 }} />
                  Fixed
                </Box>
              </Tooltip>
            )}
          </Box>
          {section.purpose && (
            <Typography sx={{ ...metaSx, mt: 0.25 }}>
              {section.purpose}
            </Typography>
          )}
        </Box>
        <Chip
          label={`${qCount} Q`}
          size="small"
          sx={{
            fontFamily: PAGE_FONT,
            fontWeight: 700,
            fontSize: 13,
            height: 24,
            borderRadius: "4px",
            bgcolor: "#F0F6FF",
            color: BLUE,
          }}
        />
        <Box sx={{ display: "flex", gap: 0.25 }}>
          {!locked && (
            <>
              <IconButton size="small" title="Edit section" onClick={onEditSection} sx={actionIconButtonSx}>
                <EditOutlinedIcon sx={actionIconSx} />
              </IconButton>
              <IconButton size="small" title="Remove section" onClick={onDeleteSection} sx={actionIconButtonSx}>
                <DeleteOutlineIcon sx={actionIconSx} />
              </IconButton>
            </>
          )}
          <IconButton size="small" onClick={onToggle}>
            {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </IconButton>
        </Box>
      </Box>
      <Collapse in={open}>
        <Box sx={{ px: 2, pb: 2, pt: 0 }}>
          {(section.items || []).length === 0 && (
            <Typography sx={{ ...metaSx, fontStyle: "italic", py: 1 }}>
              No questions yet.
            </Typography>
          )}
          {(section.items || []).map((q, qi) => (
            <Box
              key={q.id || qi}
              id={`template-question-${index}-${qi}`}
              sx={{
                py: 1,
                px: 1.5,
                mb: 0.5,
                borderRadius: 1,
                bgcolor: "#f9fafb",
                border: "1px solid #eef1f4",
                display: "flex",
                alignItems: "flex-start",
                gap: 1,
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontFamily: PAGE_FONT, fontSize: 14, lineHeight: "21px", color: TEXT }}>
                  {q.text}
                  {isRequiredQuestion(q) && (
                    <Box component="span" sx={requiredMarkSx}>*</Box>
                  )}
                </Typography>
                <Typography sx={{ ...metaSx, mt: 0.25 }}>
                  {q.required ? "required" : "optional"}
                </Typography>
              </Box>
              {!locked && (
                <>
                  <IconButton size="small" title="Edit question" onClick={() => onEditQuestion(qi)} sx={actionIconButtonSx}>
                    <EditOutlinedIcon sx={actionIconSx} />
                  </IconButton>
                  <IconButton
                    size="small"
                    title={
                      isRequiredQuestion(q)
                        ? "Required questions cannot be removed"
                        : "Remove question"
                    }
                    onClick={() => onDeleteQuestion(qi)}
                    disabled={isRequiredQuestion(q)}
                    sx={actionIconButtonSx}
                  >
                    <DeleteOutlineIcon sx={actionIconSx} />
                  </IconButton>
                </>
              )}
            </Box>
          ))}
          {!locked && (
          <Button
            variant="outlined"
            disableElevation
            disableRipple
            disableFocusRipple
            startIcon={<AddIcon />}
            onClick={onAddQuestion}
            sx={{ ...ghostBtnSx, mt: 1 }}
          >
            Add question
          </Button>
          )}
        </Box>
      </Collapse>
    </Paper>
  );
}

export default function TenantTemplateConfigPage() {
  const { id, projectId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isApplication = Boolean(projectId);
  const sidebarWidth = useContext(SidebarContext)?.sidebarWidth || 0;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [warning, setWarning] = useState("");

  const [tenant, setTenant] = useState(null);
  const [appBrief, setAppBrief] = useState("");
  const [depth, setDepth] = useState("enterprise");
  const [manualCount, setManualCount] = useState(7);
  const [capabilities, setCapabilities] = useState(["Conversational chat", "Retrieval / RAG"]);
  const [permissions, setPermissions] = useState("answer_and_edit");
  const [templateStatus, setTemplateStatus] = useState("draft");
  const [framework, setFramework] = useState(null);
  const [fixedSections, setFixedSections] = useState([]);
  const [isDirty, setIsDirty] = useState(false);
  const [openSections, setOpenSections] = useState({});

  const [sectionDialog, setSectionDialog] = useState(null);
  const [generatingQuestions, setGeneratingQuestions] = useState(false);
  const [questionDialog, setQuestionDialog] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState(null);
  const [similarityDialog, setSimilarityDialog] = useState(null);
  const [similarityLoading, setSimilarityLoading] = useState(false);
  const [showLibrary, setShowLibrary] = useState(false);
  const [reusableTemplates, setReusableTemplates] = useState([]);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [applyingTemplateId, setApplyingTemplateId] = useState(null);

  const baseUrl = isApplication
    ? `${API_BASE_URL}/projects/${projectId}/onboarding-template`
    : `${API_BASE_URL}/superadmin/tenants/${id}/onboarding-template`;

  const backPath = isApplication
    ? `/framework/${projectId}`
    : `/platform/tenants/${id}`;

  const severityChipLabel = (severity) => {
    if (severity === "block" || severity === "error" || severity === "hard_duplicate") return "Block";
    if (severity === "duplicate") return "Duplicate";
    return "Warning";
  };

  const entryLocation = (entry) => {
    const sectionIndex = entry?.section_index;
    const sectionName = entry?.section_name
      || (sectionIndex != null ? framework?.sections?.[Number(sectionIndex)]?.name : "")
      || "this template";
    if (entry?.type === "section" || entry?.question_index == null) {
      return `Section “${sectionName}”`;
    }
    return `Section “${sectionName}”, question ${Number(entry.question_index) + 1}`;
  };

  const goToSimilarityEntry = (entry) => {
    const sectionIndex = Number(entry?.section_index);
    if (!Number.isInteger(sectionIndex)) return;
    const questionIndex = entry?.question_index == null ? null : Number(entry.question_index);
    const isQuestion = entry?.type !== "section" && questionIndex != null;
    setSimilarityDialog(null);
    setOpenSections((open) => ({ ...open, [sectionIndex]: true }));
    window.setTimeout(() => {
      const targetId = isQuestion
        ? `template-question-${sectionIndex}-${questionIndex}`
        : `template-section-${sectionIndex}`;
      document.getElementById(targetId)?.scrollIntoView({ behavior: "smooth", block: "center" });
      if (isQuestion && !isLockedTemplateSection(framework?.sections?.[sectionIndex])) {
        openEditQuestion(sectionIndex, questionIndex);
      }
    }, 350);
  };

  /** @returns {Promise<{ ok: boolean, severity: string, matches?: any[], conflicts?: any[] }|null>} */
  const runCandidateCheck = async (candidate) => {
    const res = await axios.post(`${baseUrl}/similarity-check`, {
      mode: "candidate",
      framework: framework || { sections: [] },
      candidate,
    });
    return res.data?.data || null;
  };

  /** AI quality / section-fit gate (runs before duplicate detection). */
  const runQuestionValidation = async ({ text, sectionIndex }) => {
    const res = await axios.post(`${baseUrl}/validate`, {
      kind: "question",
      framework: framework || { sections: [] },
      candidate: {
        text,
        section_index: sectionIndex,
      },
    });
    return res.data?.data || null;
  };

  /** Section title/purpose shape gate (before Generate Questions and Save & Close). */
  const runSectionValidation = async ({ name, purpose }) => {
    const res = await axios.post(`${baseUrl}/validate`, {
      kind: "section",
      section_name: name,
      section_purpose: purpose || "",
    });
    return res.data?.data || null;
  };

  const showSimilarityPrompt = ({
    title,
    severity,
    message,
    matches = [],
    conflicts = [],
    allowOverride = true,
    proceedLabel,
    onProceed,
  }) => {
    setSimilarityDialog({
      title,
      severity,
      message,
      matches,
      conflicts,
      allowOverride,
      proceedLabel,
      onProceed,
    });
  };

  const handleSimilarityConflictError = (err, { title, onOverride }) => {
    const data = err.response?.data;
    if (err.response?.status === 409 && data?.code === "SIMILARITY_CONFLICT") {
      showSimilarityPrompt({
        title: title || "Similar content detected",
        severity: data.severity || "block",
        matches: data.matches || [],
        conflicts: data.conflicts || [],
        allowOverride: true,
        onProceed: onOverride,
      });
      return true;
    }
    return false;
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(baseUrl);
      const data = res.data?.data;
      const fw = data?.framework ? ensureFramework(data.framework) : null;
      const parts = fw ? partitionFramework(fw) : null;
      setTenant(data);
      setFixedSections(parts?.fixed || []);
      setFramework(parts ? parts.framework : null);
      setIsDirty(false);
      setTemplateStatus(data?.template_status || "draft");
      setPermissions(data?.template_permissions || "answer_and_edit");
      const meta = data?.meta || {};
      if (meta.app_brief) setAppBrief(meta.app_brief);
      if (meta.depth) setDepth(meta.depth);
      if (meta.section_count && meta.depth === "manual") setManualCount(meta.section_count);
      if (Array.isArray(meta.capabilities) && meta.capabilities.length) {
        setCapabilities(meta.capabilities);
      }
      if (isApplication && !meta.app_brief) {
        const gate0 = resolveGate0(projectId, location.state);
        const brief = String(gate0?.intended_use || "").trim();
        if (brief.length >= 20) setAppBrief(brief);
      }
      const needsLibrary = isApplication && !isUsableApplicationFramework(fw);
      if (needsLibrary) {
        try {
          const libRes = await axios.get(`${API_BASE_URL}/projects/templates/reusable`, {
            params: { excludeProjectId: projectId },
          });
          setReusableTemplates(Array.isArray(libRes.data?.data) ? libRes.data.data : []);
        } catch (libErr) {
          setReusableTemplates([]);
          setError(libErr.response?.data?.error || "Could not load existing templates.");
        }
        setShowLibrary(true);
      } else {
        setReusableTemplates([]);
        setShowLibrary(false);
      }
    } catch (e) {
      setError(e.response?.data?.error || "Failed to load template.");
    } finally {
      setLoading(false);
    }
  }, [baseUrl, isApplication, projectId, location.state]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!isDirty) return undefined;
    const onBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [isDirty]);

  const totals = useMemo(() => {
    const sections = framework?.sections || [];
    const questions = sections.reduce((n, s) => n + (s.items?.length || 0), 0);
    return { sections: sections.length, questions };
  }, [framework]);

  const updateFrameworkSections = (updater) => {
    setFramework((prev) => {
      const base = ensureFramework(prev);
      const sections = updater(base.sections || []);
      return { ...base, sections };
    });
    setIsDirty(true);
  };

  const toggleCap = (cap) => {
    setCapabilities((prev) =>
      prev.includes(cap) ? prev.filter((c) => c !== cap) : [...prev, cap]
    );
  };

  const applyDefaultSections = () => {
    const preset = getPresetFramework("standard");
    setFramework(preset);
    setIsDirty(true);
    const open = {};
    (preset?.sections || []).forEach((s, i) => { open[i] = i === 0; });
    setOpenSections(open);
  };

  const handleGenerate = async () => {
    if (depth === "defaults") return;
    if (!hasSelectedCapability(capabilities)) return;
    if (appBrief.trim().length < 20) {
      setError("Describe the application in at least 20 characters.");
      return;
    }
    setGenerating(true);
    setError("");
    setSuccess("");
    try {
      const res = await axios.post(`${baseUrl}/generate`, {
        app_brief: appBrief.trim(),
        depth,
        manual_section_count: depth === "manual" ? manualCount : undefined,
        capabilities,
      });
      const data = res.data?.data;
      setFramework(partitionFramework(data?.framework).framework);
      setIsDirty(false);
      setTemplateStatus(data?.template_status || "draft");
      const open = {};
      (data?.framework?.sections || []).forEach((s, i) => { open[i] = i === 0; });
      setOpenSections(open);

      const similarity = res.data?.similarity;
      if (similarity && !similarity.ok) {
        showSimilarityPrompt({
          title: "Generated template has similar sections/questions",
          severity: similarity.severity,
          conflicts: similarity.conflicts || [],
          allowOverride: false,
          onProceed: null,
        });
        setSuccess("Sections generated — review similar items below before publishing.");
      } else {
        setSuccess("Sections generated — review below, then publish when ready.");
      }
    } catch (e) {
      setError(e.response?.data?.error || "Generation failed.");
    } finally {
      setGenerating(false);
    }
  };

  const persistDraft = async ({ allowOverride = false } = {}) => {
    const body = {
      framework: attachFixedSections(framework, fixedSections),
      meta: { app_brief: appBrief.trim(), depth, capabilities },
      allow_similarity_override: allowOverride,
    };
    if (!isApplication) {
      body.template_permissions = permissions;
    }
    await axios.put(baseUrl, body);
  };

  const handlePublish = async ({ allowOverride = false } = {}) => {
    const frameworkToStore = attachFixedSections(framework, fixedSections);
    if (!frameworkToStore.sections?.length) {
      setError("Generate a template first.");
      return;
    }
    setSaving(true);
    setPublishing(true);
    setError("");
    let leaveOnSuccess = false;
    try {
      await persistDraft({ allowOverride });
      const publishBody = {
        template_status: "published",
        framework: frameworkToStore,
        allow_similarity_override: allowOverride,
      };
      if (!isApplication) {
        publishBody.template_permissions = permissions;
      }
      const res = await axios.put(`${baseUrl}/publish`, publishBody);
      const data = res.data?.data;
      setTemplateStatus(data?.template_status || "published");
      setIsDirty(false);
      setSimilarityDialog(null);
      if (isApplication) {
        leaveOnSuccess = true;
        navigate(`/framework/${projectId}`, { state: location.state });
        return;
      }
      setSuccess(
        `Published to ${tenant?.org_name || "tenant"}. New apps will inherit this template.`
      );
    } catch (e) {
      if (
        handleSimilarityConflictError(e, {
          title: "Cannot publish — similar content found",
          onOverride: () => handlePublish({ allowOverride: true }),
        })
      ) {
        return;
      }
      setError(e.response?.data?.error || "Publish failed.");
    } finally {
      if (!leaveOnSuccess) {
        setSaving(false);
        setPublishing(false);
      }
    }
  };

  const enterEditorWithFramework = (fw, { dirty, status, message } = {}) => {
    const parts = partitionFramework(fw);
    setFixedSections(parts.fixed);
    setFramework(parts.framework);
    setIsDirty(Boolean(dirty));
    if (status) setTemplateStatus(status);
    setShowLibrary(false);
    setPreviewTemplate(null);
    const open = {};
    (parts.framework.sections || []).forEach((s, i) => { open[i] = i === 0; });
    setOpenSections(open);
    if (message) setSuccess(message);
  };

  const handleUseTemplate = async (item, { allowOverride = false } = {}) => {
    if (!item?.framework) {
      setError("This template has no framework to copy.");
      return;
    }
    const clone = cloneFramework(item.framework);
    setApplyingTemplateId(item.project_id);
    setError("");
    try {
      const res = await axios.put(baseUrl, {
        framework: clone,
        source_project_id: item.project_id,
        allow_similarity_override: allowOverride,
      });
      const data = res.data?.data;
      if (data) setTenant(data);
      enterEditorWithFramework(data?.framework || clone, {
        dirty: false,
        status: data?.template_status || "draft",
        message: `Copied “${item.project_name}” into this application. The original template is unchanged.`,
      });
      setSimilarityDialog(null);
    } catch (e) {
      if (
        handleSimilarityConflictError(e, {
          title: "Similar content in this template",
          onOverride: () => handleUseTemplate(item, { allowOverride: true }),
        })
      ) {
        enterEditorWithFramework(clone, { dirty: true, status: "draft" });
        return;
      }
      setError(e.response?.data?.error || "Could not use this template.");
    } finally {
      setApplyingTemplateId(null);
    }
  };

  const handleStartFromScratch = () => {
    setShowLibrary(false);
    setPreviewTemplate(null);
    setError("");
  };

  const handleSaveDraft = async ({ allowOverride = false } = {}) => {
    if (!framework) return;
    setSaving(true);
    setError("");
    try {
      await persistDraft({ allowOverride });
      setIsDirty(false);
      setSimilarityDialog(null);
      setSuccess("Draft saved.");
    } catch (e) {
      if (
        handleSimilarityConflictError(e, {
          title: "Near-duplicate content — save blocked",
          onOverride: () => handleSaveDraft({ allowOverride: true }),
        })
      ) {
        return;
      }
      setError(e.response?.data?.error || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const openAddSection = () => {
    setError("");
    setSectionDialog({ mode: "add", name: "", purpose: "", items: [] });
  };

  const openEditSection = (index) => {
    const sec = framework?.sections?.[index];
    if (!sec || isLockedTemplateSection(sec)) return;
    setSectionDialog({ mode: "edit", index, name: sec.name || "", purpose: sec.purpose || "" });
  };

  /** One-shot Generate Questions (Add Section) — same redraft endpoint, no instruction dialog. */
  const handleGenerateQuestions = async () => {
    if (depth === "defaults") return;
    const name = sectionDialog?.name?.trim();
    if (!name || sectionDialog?.mode !== "add") return;
    const purpose = sectionDialog?.purpose?.trim() || "";

    setGeneratingQuestions(true);
    setError("");
    try {
      const validation = await runSectionValidation({ name, purpose });
      if (validation && validation.ok === false && validation.severity === "error") {
        setError(
          validation.checks?.quality?.reason
            || "This does not appear to be a meaningful section title."
        );
        return;
      }

      const res = await axios.post(`${baseUrl}/redraft`, {
        kind: "new_questions",
        section_name: name,
        section_purpose: purpose,
        framework: framework || { sections: [] },
        app_brief: appBrief.trim(),
        capabilities,
      });
      const questions = res.data?.data?.draft?.questions;
      if (!Array.isArray(questions) || !questions.length) {
        throw new Error("No questions returned.");
      }
      setSectionDialog((d) => ({
        ...d,
        items: questions.map((q, i) => ({
          id: `draft_q_${Date.now()}_${i}`,
          text: String(q.text || "").trim(),
          required: q.required !== false,
        })).filter((q) => q.text),
      }));
    } catch (e) {
      setError(e.response?.data?.error || e.message || "Failed to generate questions.");
    } finally {
      setGeneratingQuestions(false);
    }
  };

  const addDialogQuestion = () => {
    setSectionDialog((d) => ({
      ...d,
      items: [
        ...(d.items || []),
        { id: `draft_q_${Date.now()}`, text: "", required: true },
      ],
    }));
  };

  const updateDialogQuestion = (index, patch) => {
    setSectionDialog((d) => ({
      ...d,
      items: (d.items || []).map((q, i) => (i === index ? { ...q, ...patch } : q)),
    }));
  };

  const removeDialogQuestion = (index) => {
    setSectionDialog((d) => {
      const q = d?.items?.[index];
      if (isRequiredQuestion(q)) return d;
      return {
        ...d,
        items: (d.items || []).filter((_, i) => i !== index),
      };
    });
  };

  const applySectionDialog = () => {
    const name = sectionDialog?.name?.trim();
    if (!name) return;
    const purpose = sectionDialog?.purpose?.trim() || "";
    if (sectionDialog.mode === "add") {
      updateFrameworkSections((sections) => {
        const sec = createSection(sections);
        const items = [];
        for (const q of sectionDialog.items || []) {
          const text = String(q.text || "").trim();
          if (!text) continue;
          const item = createItem({ ...sec, items });
          items.push({
            ...item,
            text,
            required: q.required !== false,
          });
        }
        return [
          ...sections,
          { ...sec, name, purpose, items, items_count: items.length },
        ];
      });
      setOpenSections((o) => ({ ...o, [framework?.sections?.length || 0]: true }));
      setSuccess(`Added section "${name}".`);
    } else {
      const idx = sectionDialog.index;
      updateFrameworkSections((sections) =>
        sections.map((s, i) => (i === idx ? { ...s, name, purpose } : s))
      );
      setSuccess("Section updated.");
    }
    setSectionDialog(null);
    setSimilarityDialog(null);
  };

  const saveSectionDialog = async () => {
    const name = sectionDialog?.name?.trim();
    if (!name) return;
    const purpose = sectionDialog?.purpose?.trim() || "";
    const excludeSectionId =
      sectionDialog.mode === "edit"
        ? framework?.sections?.[sectionDialog.index]?.id
        : undefined;

    setSimilarityLoading(true);
    setError("");
    try {
      const validation = await runSectionValidation({ name, purpose });
      if (validation && validation.ok === false && validation.severity === "error") {
        setError(
          validation.checks?.quality?.reason
            || "This does not appear to be a meaningful section title."
        );
        return;
      }

      const result = await runCandidateCheck({
        kind: "section",
        name,
        purpose,
        exclude_section_id: excludeSectionId,
      });
      if (result && !result.ok) {
        showSimilarityPrompt({
          title: "This section looks similar to an existing one",
          severity: result.severity,
          matches: result.matches || [],
          allowOverride: true,
          onProceed: applySectionDialog,
        });
        return;
      }
      applySectionDialog();
    } catch (e) {
      setError(e.response?.data?.error || "Section validation or similarity check failed.");
    } finally {
      setSimilarityLoading(false);
    }
  };

  const confirmDeleteSection = (index) => {
    const sec = framework?.sections?.[index];
    if (isLockedTemplateSection(sec)) return;
    setDeleteDialog({
      title: "Remove section?",
      message: `Remove "${sec?.name || "this section"}" and all its questions?`,
      onConfirm: () => {
        updateFrameworkSections((sections) => sections.filter((_, i) => i !== index));
        setDeleteDialog(null);
        setSuccess("Section removed.");
      },
    });
  };

  const openAddQuestion = (sectionIndex) => {
    if (isLockedTemplateSection(framework?.sections?.[sectionIndex])) return;
    setQuestionDialog({
      mode: "add",
      sectionIndex,
      text: "",
      required: true,
    });
  };

  const openEditQuestion = (sectionIndex, questionIndex) => {
    if (isLockedTemplateSection(framework?.sections?.[sectionIndex])) return;
    const q = framework?.sections?.[sectionIndex]?.items?.[questionIndex];
    if (!q) return;
    setQuestionDialog({
      mode: "edit",
      sectionIndex,
      questionIndex,
      itemId: q.id != null && String(q.id) !== "" ? String(q.id) : null,
      text: q.text || "",
      required: q.required !== false,
    });
  };

  const applyQuestionDialog = (opts = {}) => {
    const text = questionDialog?.text?.trim();
    if (!text) return;
    const { sectionIndex, questionIndex, mode, required } = questionDialog;
    updateFrameworkSections((sections) =>
      sections.map((s, si) => {
        if (si !== sectionIndex) return s;
        if (mode === "add") {
          const item = createItem(s);
          return {
            ...s,
            items: [...(s.items || []), { ...item, text, required }],
            items_count: (s.items?.length || 0) + 1,
          };
        }
        const items = (s.items || []).map((it, qi) =>
          qi === questionIndex ? { ...it, text, required } : it
        );
        return { ...s, items };
      })
    );
    setQuestionDialog(null);
    setSimilarityDialog(null);
    if (opts.semanticUnavailable) {
      const action = mode === "edit" ? "updated" : "added";
      setWarning(
        opts.unavailableMessage
          || `Semantic validation is currently unavailable. The question was ${action} without duplicate checking.`
      );
    } else {
      setSuccess(mode === "add" ? "Question added." : "Question updated.");
    }
  };

  /** Existing near-duplicate gate — called after AI validation passes or is overridden. */
  const runQuestionDuplicateGate = async ({ validationUnavailable = false } = {}) => {
    const text = questionDialog?.text?.trim();
    if (!text) return;
    const { sectionIndex, questionIndex, mode, itemId } = questionDialog;
    const liveSection = framework?.sections?.[sectionIndex];
    const liveItem = liveSection?.items?.[questionIndex];
    const excludeItemId =
      mode === "edit"
        ? (itemId
            || (liveItem?.id != null && String(liveItem.id) !== ""
              ? String(liveItem.id)
              : null))
        : null;

    setSimilarityLoading(true);
    setError("");
    try {
      const candidate = {
        kind: "question",
        text,
      };
      if (mode === "edit") {
        if (excludeItemId) candidate.exclude_item_id = excludeItemId;
        if (liveSection?.id != null && String(liveSection.id) !== "") {
          candidate.exclude_section_id = String(liveSection.id);
        }
        // Index fallback so the edited row is excluded even if id is missing/mismatched.
        candidate.exclude_section_index = sectionIndex;
        candidate.exclude_question_index = questionIndex;
      }

      const result = await runCandidateCheck(candidate);
      if (result && !result.ok) {
        const sev = result.severity;
        if (sev === "hard_duplicate") {
          showSimilarityPrompt({
            title: "Hard duplicate question detected",
            severity: "hard_duplicate",
            message:
              "This question is nearly identical to an existing one and cannot be added as written. Rewrite the question to continue.",
            matches: result.matches || [],
            allowOverride: false,
          });
          return;
        }
        if (sev === "duplicate") {
          showSimilarityPrompt({
            title: "Duplicate question detected",
            severity: "duplicate",
            message:
              "This question matches an existing one closely enough to be treated as a duplicate. Cancel to rewrite, or keep anyway if intentional.",
            matches: result.matches || [],
            allowOverride: true,
            proceedLabel: "Keep anyway",
            onProceed: () => applyQuestionDialog({
              semanticUnavailable: validationUnavailable || Boolean(result?.validation_unavailable),
              unavailableMessage: validationUnavailable
                ? `Semantic validation is currently unavailable. The question was ${mode === "edit" ? "updated" : "added"} without full validation.`
                : undefined,
            }),
          });
          return;
        }
        showSimilarityPrompt({
          title: "This question looks similar to an existing one",
          severity: sev || "warn",
          matches: result.matches || [],
          allowOverride: true,
          proceedLabel: "Keep anyway",
          onProceed: () => applyQuestionDialog({
            semanticUnavailable: validationUnavailable || Boolean(result?.validation_unavailable),
            unavailableMessage: validationUnavailable
              ? `Semantic validation is currently unavailable. The question was ${mode === "edit" ? "updated" : "added"} without full validation.`
              : undefined,
          }),
        });
        return;
      }
      applyQuestionDialog({
        semanticUnavailable: validationUnavailable || Boolean(result?.validation_unavailable),
        unavailableMessage: validationUnavailable && !result?.validation_unavailable
          ? `Semantic validation is currently unavailable. The question was ${mode === "edit" ? "updated" : "added"} without full quality/section checking.`
          : undefined,
      });
    } catch (e) {
      setError(e.response?.data?.error || "Similarity check failed.");
    } finally {
      setSimilarityLoading(false);
    }
  };

  const saveQuestionDialog = async () => {
    const text = questionDialog?.text?.trim();
    if (!text) return;
    const { sectionIndex, mode } = questionDialog;

    setSimilarityLoading(true);
    setError("");
    try {
      const validation = await runQuestionValidation({ text, sectionIndex });
      const severity = validation?.severity || (validation?.ok ? "ok" : "error");
      const checks = validation?.checks || {};
      const validationUnavailable = Boolean(validation?.validation_unavailable);

      if (severity === "error") {
        const reason =
          checks.quality?.reason ||
          "This doesn't appear to be a meaningful onboarding question.";
        showSimilarityPrompt({
          title: "Invalid question",
          severity: "error",
          message: reason,
          allowOverride: false,
        });
        return;
      }

      if (severity === "warn") {
        const fit = checks.section_fit || {};
        let reason =
          fit.reason ||
          "This question appears to belong under a different section.";
        if (fit.suggested_section && !reason.includes(fit.suggested_section)) {
          reason = `${reason} Suggested section: ${fit.suggested_section}.`;
        }
        showSimilarityPrompt({
          title: "Question may belong in another section",
          severity: "warn",
          message: reason,
          allowOverride: true,
          proceedLabel: mode === "edit" ? "Save anyway" : "Add Anyway",
          onProceed: () => {
            void runQuestionDuplicateGate({ validationUnavailable });
          },
        });
        return;
      }

      await runQuestionDuplicateGate({ validationUnavailable });
    } catch (e) {
      setError(e.response?.data?.error || "Question validation failed.");
    } finally {
      setSimilarityLoading(false);
    }
  };

  const confirmDeleteQuestion = (sectionIndex, questionIndex) => {
    if (isLockedTemplateSection(framework?.sections?.[sectionIndex])) return;
    const q = framework?.sections?.[sectionIndex]?.items?.[questionIndex];
    if (isRequiredQuestion(q)) {
      setError("Required questions cannot be removed. Edit the question and turn off Required to delete it.");
      return;
    }
    setDeleteDialog({
      title: "Remove question?",
      message: "This question will be removed from the template.",
      onConfirm: () => {
        const current = framework?.sections?.[sectionIndex]?.items?.[questionIndex];
        if (isRequiredQuestion(current)) {
          setDeleteDialog(null);
          return;
        }
        updateFrameworkSections((sections) =>
          sections.map((s, si) => {
            if (si !== sectionIndex) return s;
            const items = (s.items || []).filter((_, qi) => qi !== questionIndex);
            return { ...s, items, items_count: items.length };
          })
        );
        setDeleteDialog(null);
        setSuccess("Question removed.");
      },
    });
  };

  const pageNotices = (
    <>
      <Snackbar
        open={Boolean(error) && !sectionDialog}
        onClose={() => setError("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        sx={{
          left: `${sidebarWidth + 16}px !important`,
          bottom: "80px !important",
        }}
      >
        <Alert
          severity="error"
          onClose={() => setError("")}
          sx={{ fontFamily: PAGE_FONT, fontSize: 14 }}
        >
          {error}
        </Alert>
      </Snackbar>
      <IntakeSuccessToast
        open={Boolean(success)}
        message={success}
        onClose={() => setSuccess("")}
        left={sidebarWidth + 24}
        bottom={80}
      />
      <IntakeSuccessToast
        open={Boolean(warning)}
        message={warning}
        type="warning"
        onClose={() => setWarning("")}
        autoHideMs={7000}
        left={sidebarWidth + 24}
        bottom={80}
      />
    </>
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (isApplication && showLibrary) {
    return (
      <>
        <ApplicationTemplateLibrary
          templates={reusableTemplates}
          error={error}
          applyingId={applyingTemplateId}
          preview={previewTemplate}
          onBack={() => navigate(backPath, { state: location.state })}
          onView={setPreviewTemplate}
          onClosePreview={() => setPreviewTemplate(null)}
          onUse={(item) => { void handleUseTemplate(item); }}
          onScratch={handleStartFromScratch}
        />
        {pageNotices}
      </>
    );
  }

  return (
    <Box sx={{ bgcolor: "#F5F5F5", minHeight: "100vh", pb: 10, ...pageTypeSx }}>
      <Box sx={{ bgcolor: "#fff", borderBottom: "1px solid #E6E6E6", px: 3, py: 1.5, display: "flex", alignItems: "center", gap: 2 }}>
        <IconButton size="small" onClick={() => navigate(backPath, isApplication ? { state: location.state } : undefined)} sx={{ color: TEXT }}>
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography sx={pageTitleSx}>
            {isApplication ? "Application onboarding template" : "Onboarding template"}
            {" — "}
            {tenant?.org_name || (isApplication ? "Application" : "Tenant")}
          </Typography>
          <Typography sx={metaSx}>
            {isApplication ? `App #${projectId}` : tenant?.slug}
            {" · "}
            {templateStatus === "published" ? "PUBLISHED" : "DRAFT"}
            {totals.sections > 0 && ` · ${totals.sections} sections · ${totals.questions} questions`}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          disableElevation
          disableRipple
          disableFocusRipple
          onClick={handleSaveDraft}
          disabled={saving || !framework}
          sx={ghostBtnSx}
        >
          Save draft
        </Button>
      </Box>

      <Box sx={{ maxWidth: 840, mx: "auto", px: 3, py: 4 }}>
        <Typography variant="h6" sx={{ mb: 0.5 }}>
          Application overview
          <Box component="span" sx={requiredMarkSx}>
            *
          </Box>
        </Typography>
        <Typography sx={{ ...bodySx, mb: 2 }}>
          {isApplication
            ? "What this AI application does, who uses it, and what must never go wrong. This seeds AI-generated sections."
            : "What their AI apps do, who uses them, and what must never go wrong. This seeds AI-generated sections."}
        </Typography>
        <TextField
          fullWidth
          multiline
          minRows={5}
          value={appBrief}
          onChange={(e) => setAppBrief(e.target.value)}
          placeholder="e.g. Retail lending assistants for branch staff — RAG over policy handbooks, must cite sources, no PII exposure..."
          inputProps={{ "aria-required": true }}
          sx={{ mb: 4, bgcolor: "#fff" }}
        />

        <Typography variant="h6" sx={{ mb: 0.5 }}>
          Evaluation framework
          <Box component="span" sx={requiredMarkSx}>
            *
          </Box>
        </Typography>
        <Typography sx={{ ...bodySx, mb: 2 }}>
          Uses existing presets as AI depth hints (Lightweight, Standard, Enterprise, etc.).
        </Typography>
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 1, mb: depth === "manual" ? 1 : 4 }}>
          {DEPTH_OPTIONS.map((opt) => (
            <Paper
              key={opt.key}
              variant="outlined"
              onClick={() => {
                setDepth(opt.key);
                if (opt.key === "defaults") applyDefaultSections();
              }}
              sx={{
                p: 1.5,
                cursor: "pointer",
                borderColor: depth === opt.key ? BLUE : "#E6E6E6",
                bgcolor: depth === opt.key ? "#F0F6FF" : "#fff",
                boxShadow: depth === opt.key ? `inset 0 0 0 1px ${BLUE}` : "none",
              }}
            >
              <Typography sx={{ fontFamily: PAGE_FONT, fontWeight: 700, fontSize: 14, lineHeight: "21px", color: depth === opt.key ? BLUE : TEXT }}>
                {opt.label}
              </Typography>
              <Typography sx={{ ...metaSx, fontSize: 13 }}>{opt.hint}</Typography>
            </Paper>
          ))}
        </Box>
        {depth === "manual" && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 4 }}>
            <Typography sx={bodySx}>How many sections?</Typography>
            <TextField
              type="number"
              size="small"
              inputProps={{ min: 1, max: 20 }}
              value={manualCount}
              onChange={(e) => setManualCount(Number(e.target.value))}
              sx={{ width: 80, bgcolor: "#fff" }}
            />
          </Box>
        )}

        <Typography variant="h6" sx={{ mb: 0.5 }}>
          System capabilities
          <Box component="span" sx={requiredMarkSx}>
            *
          </Box>
        </Typography>
        <Typography sx={{ ...bodySx, mb: 2 }}>Select capabilities that apply.</Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1 }}>
          {CAPABILITIES.map((cap) => (
            <Chip
              key={cap}
              label={cap}
              onClick={() => toggleCap(cap)}
              variant={capabilities.includes(cap) ? "filled" : "outlined"}
              sx={{
                cursor: "pointer",
                fontFamily: PAGE_FONT,
                fontWeight: 700,
                fontSize: 13,
                height: 32,
                borderRadius: "4px",
                border: `1px solid ${capabilities.includes(cap) ? BLUE : "#E6E6E6"}`,
                bgcolor: capabilities.includes(cap) ? BLUE : "#FFFFFF",
                color: capabilities.includes(cap) ? "#FFFFFF" : TEXT,
                "&:hover": {
                  bgcolor: capabilities.includes(cap) ? "#0046B8" : "#F0F6FF",
                },
              }}
            />
          ))}
        </Box>
        {!hasSelectedCapability(capabilities) && (
          <Typography
            id="generate-sections-capability-hint"
            component="p"
            role="status"
            sx={{ ...bodySx, mb: 1.5, color: "#DE350B" }}
          >
            {CAPABILITY_REQUIRED_MESSAGE}
          </Typography>
        )}
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 4 }}>
          <Button
            variant="contained"
            disableElevation
            disableRipple
            disableFocusRipple
            startIcon={generating ? <CircularProgress size={16} sx={{ color: "#FFFFFF" }} /> : <AutoAwesomeIcon />}
            onClick={handleGenerate}
            disabled={generating || depth === "defaults" || !hasSelectedCapability(capabilities)}
            aria-describedby={!hasSelectedCapability(capabilities) ? "generate-sections-capability-hint" : undefined}
            sx={primaryBtnSx}
          >
            {generating ? "Generating…" : "Generate sections"}
          </Button>
        </Box>

        {(framework?.sections?.length > 0 || framework) && (
          <>
            <Box sx={{ borderTop: "1px solid #dde4ec", pt: 3, mb: 2 }}>
              <Typography variant="h6">
                Sections
                <Typography component="span" sx={{ ...metaSx, ml: 1 }}>
                  {totals.sections} · {totals.questions} questions
                </Typography>
              </Typography>
              <Typography sx={{ ...bodySx, mb: 2 }}>
                Expand to review questions. EAO still gets the 52 fixed FR sections in their config screen.
              </Typography>
            </Box>
            {(framework?.sections || []).map((sec, i) => (
              <SectionBlock
                key={sec.id || i}
                section={sec}
                index={i}
                open={Boolean(openSections[i])}
                onToggle={() => setOpenSections((o) => ({ ...o, [i]: !o[i] }))}
                onEditSection={() => openEditSection(i)}
                onDeleteSection={() => confirmDeleteSection(i)}
                onEditQuestion={(qi) => openEditQuestion(i, qi)}
                onDeleteQuestion={(qi) => confirmDeleteQuestion(i, qi)}
                onAddQuestion={() => openAddQuestion(i)}
              />
            ))}
            <Paper
              variant="outlined"
              sx={{
                mt: 2,
                p: 2,
                borderStyle: "dashed",
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
              }}
            >
              <Button
                variant="outlined"
                disableElevation
                disableRipple
                disableFocusRipple
                startIcon={<AddIcon />}
                onClick={openAddSection}
                sx={ghostBtnSx}
              >
                Add section
              </Button>
            </Paper>
          </>
        )}

        {!isApplication && (
          <Box sx={{ borderTop: "1px solid #dde4ec", pt: 3, mt: 3 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>Client permissions</Typography>
            <Typography sx={{ ...bodySx, mb: 2 }}>
              Controls whether the tenant EAO can edit framework structure (intake chat is always allowed).
            </Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 1, mb: 3 }}>
              {PERMISSION_OPTIONS.map((p) => (
                <Paper
                  key={p.value}
                  variant="outlined"
                  onClick={() => setPermissions(p.value)}
                  sx={{
                    p: 1.5,
                    cursor: "pointer",
                    borderColor: permissions === p.value ? BLUE : "#E6E6E6",
                    bgcolor: permissions === p.value ? "#F0F6FF" : "#fff",
                    boxShadow: permissions === p.value ? `inset 0 0 0 1px ${BLUE}` : "none",
                  }}
                >
                  <Typography sx={{ fontFamily: PAGE_FONT, fontWeight: 700, fontSize: 14, lineHeight: "21px", color: permissions === p.value ? BLUE : TEXT }}>{p.label}</Typography>
                  <Typography sx={metaSx}>{p.desc}</Typography>
                </Paper>
              ))}
            </Box>
          </Box>
        )}
      </Box>

      <Box
        sx={{
          position: "fixed",
          bottom: 0,
          left: sidebarWidth,
          right: 0,
          zIndex: 10,
          height: 56,
          boxSizing: "border-box",
          bgcolor: "#FFFFFF",
          borderTop: "1px solid #E6E6E6",
          boxShadow: "0px -1px 8px 0px #2122260A",
          px: "24px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "12px",
        }}
      >
        <Typography sx={{ ...metaSx, flex: 1 }}>
          {templateStatus === "published" ? (
            <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
              <CheckCircleOutlineIcon fontSize="small" sx={{ color: BLUE }} />
              {isApplication
                ? "Published — this application uses this template"
                : "Published — tenant apps use this template"}
            </Box>
          ) : (
            <>
              Draft — publish when ready for {tenant?.org_name || (isApplication ? "this application" : "tenant")}
            </>
          )}
        </Typography>
        <Button
          variant="outlined"
          disableElevation
          disableRipple
          disableFocusRipple
          onClick={handleSaveDraft}
          disabled={saving || !framework}
          sx={ghostBtnSx}
        >
          Save draft
        </Button>
        <Button
          variant="contained"
          disableElevation
          disableRipple
          disableFocusRipple
          onClick={() => handlePublish()}
          disabled={saving || !attachFixedSections(framework, fixedSections).sections?.length}
          startIcon={publishing ? <CircularProgress size={16} sx={{ color: "#FFFFFF" }} /> : null}
          sx={primaryBtnSx}
        >
          {publishing ? "Publishing…" : isApplication ? "Publish to Application" : "Publish to tenant"}
        </Button>
      </Box>

      <Dialog
        open={Boolean(sectionDialog)}
        onClose={() => !generatingQuestions && !similarityLoading && setSectionDialog(null)}
        maxWidth={sectionDialog?.mode === "add" ? "md" : "sm"}
        fullWidth
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogTitle>{sectionDialog?.mode === "add" ? "Add section" : "Edit section"}</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mt: 1, mb: 2 }} onClose={() => setError("")}>
              {error}
            </Alert>
          )}
          <TextField
            autoFocus
            fullWidth
            label="Section title"
            value={sectionDialog?.name || ""}
            onChange={(e) => setSectionDialog((d) => ({ ...d, name: e.target.value }))}
            disabled={generatingQuestions}
            sx={{ mt: error ? 0 : 1, mb: 2 }}
          />
          <TextField
            fullWidth
            multiline
            minRows={2}
            label="Why this section matters"
            value={sectionDialog?.purpose || ""}
            onChange={(e) => setSectionDialog((d) => ({ ...d, purpose: e.target.value }))}
            disabled={generatingQuestions}
            sx={{ mb: sectionDialog?.mode === "add" ? 2 : 0 }}
          />

          {sectionDialog?.mode === "add" && (
            <Box>
              <Button
                variant="outlined"
                disableElevation
                disableRipple
                disableFocusRipple
                startIcon={
                  generatingQuestions
                    ? <CircularProgress size={16} sx={{ color: BLUE }} />
                    : <AutoAwesomeIcon />
                }
                onClick={handleGenerateQuestions}
                disabled={generatingQuestions || depth === "defaults" || !sectionDialog?.name?.trim()}
                sx={{ ...ghostBtnSx, mb: 2 }}
              >
                {generatingQuestions ? "Generating…" : "Generate Questions"}
              </Button>

              <Typography sx={{ fontFamily: PAGE_FONT, fontWeight: 700, fontSize: 15, lineHeight: "24px", color: TEXT, mb: 1 }}>
                Questions
                <Typography component="span" sx={{ ...metaSx, ml: 1 }}>
                  {(sectionDialog.items || []).length}
                </Typography>
              </Typography>

              {(sectionDialog.items || []).length === 0 && !generatingQuestions && (
                <Typography sx={{ ...bodySx, fontStyle: "italic", mb: 1 }}>
                  No questions yet — generate with AI or add manually.
                </Typography>
              )}

              {(sectionDialog.items || []).map((q, qi) => (
                <Box
                  key={q.id || qi}
                  sx={{
                    mb: 1.5,
                    p: 1.5,
                    borderRadius: 1,
                    border: "1px solid #eef1f4",
                    bgcolor: "#f9fafb",
                  }}
                >
                  <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    size="small"
                    label={`Question ${qi + 1}`}
                    value={q.text || ""}
                    onChange={(e) => updateDialogQuestion(qi, { text: e.target.value })}
                    disabled={generatingQuestions}
                    sx={{ mb: 0.5 }}
                  />
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={q.required !== false}
                          onChange={(e) => updateDialogQuestion(qi, { required: e.target.checked })}
                          disabled={generatingQuestions}
                        />
                      }
                      label="Required"
                    />
                    <IconButton
                      size="small"
                      title={
                        isRequiredQuestion(q)
                          ? "Required questions cannot be removed"
                          : "Remove question"
                      }
                      onClick={() => removeDialogQuestion(qi)}
                      disabled={generatingQuestions || isRequiredQuestion(q)}
                    >
                      <DeleteOutlineIcon sx={actionIconSx} />
                    </IconButton>
                  </Box>
                </Box>
              ))}

              <Button
                variant="outlined"
                disableElevation
                disableRipple
                disableFocusRipple
                startIcon={<AddIcon />}
                onClick={addDialogQuestion}
                disabled={generatingQuestions}
                sx={ghostBtnSx}
              >
                Add Question
              </Button>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            disableElevation
            disableRipple
            disableFocusRipple
            onClick={() => setSectionDialog(null)}
            disabled={similarityLoading || generatingQuestions}
            sx={ghostBtnSx}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disableElevation
            disableRipple
            disableFocusRipple
            onClick={saveSectionDialog}
            disabled={!sectionDialog?.name?.trim() || similarityLoading || generatingQuestions}
            startIcon={similarityLoading ? <CircularProgress size={16} sx={{ color: "#FFFFFF" }} /> : null}
            sx={primaryBtnSx}
          >
            {similarityLoading
              ? "Checking…"
              : sectionDialog?.mode === "add"
                ? "Save & Close"
                : "Save changes"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(questionDialog)} onClose={() => setQuestionDialog(null)} maxWidth="sm" fullWidth PaperProps={{ sx: dialogPaperSx }}>
        <DialogTitle>{questionDialog?.mode === "add" ? "Add question" : "Edit question"}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={3}
            label="Question"
            value={questionDialog?.text || ""}
            onChange={(e) => setQuestionDialog((d) => ({ ...d, text: e.target.value }))}
            sx={{ mt: 1, mb: 1 }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={questionDialog?.required !== false}
                onChange={(e) => setQuestionDialog((d) => ({ ...d, required: e.target.checked }))}
              />
            }
            label="Required"
          />
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            disableElevation
            disableRipple
            disableFocusRipple
            onClick={() => setQuestionDialog(null)}
            disabled={similarityLoading}
            sx={ghostBtnSx}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disableElevation
            disableRipple
            disableFocusRipple
            onClick={saveQuestionDialog}
            disabled={!questionDialog?.text?.trim() || similarityLoading}
            startIcon={similarityLoading ? <CircularProgress size={16} sx={{ color: "#FFFFFF" }} /> : null}
            sx={primaryBtnSx}
          >
            {similarityLoading
              ? "Checking…"
              : questionDialog?.mode === "add"
                ? "Add question"
                : "Save changes"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteDialog)} onClose={() => setDeleteDialog(null)} PaperProps={{ sx: dialogPaperSx }}>
        <DialogTitle>{deleteDialog?.title}</DialogTitle>
        <DialogContent>
          <Typography sx={bodySx}>{deleteDialog?.message}</Typography>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            disableElevation
            disableRipple
            disableFocusRipple
            onClick={() => setDeleteDialog(null)}
            sx={ghostBtnSx}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disableElevation
            disableRipple
            disableFocusRipple
            onClick={() => deleteDialog?.onConfirm?.()}
            sx={primaryBtnSx}
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(similarityDialog)}
        onClose={() => setSimilarityDialog(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: dialogPaperSx }}
      >
        {similarityDialog && (
          <>
            <DialogTitle>{similarityDialog.title}</DialogTitle>
            <DialogContent>
              <Alert
                severity={
                  similarityDialog.severity === "block"
                    || similarityDialog.severity === "error"
                    || similarityDialog.severity === "hard_duplicate"
                    || similarityDialog.severity === "duplicate"
                    ? "error"
                    : "warning"
                }
                sx={{ mb: 2 }}
              >
                {similarityDialog.message
                  || (similarityDialog.severity === "hard_duplicate"
                    ? "This question is nearly identical to an existing one and cannot be added as written."
                    : similarityDialog.severity === "duplicate" || similarityDialog.severity === "block"
                      ? "These look nearly identical. Rewrite for clearer onboarding mapping, or override if intentional."
                      : "These may overlap in meaning. Review before continuing so intake answers map cleanly.")}
              </Alert>
              {(similarityDialog.matches || []).length > 0 && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mb: 1 }}>
                  {similarityDialog.matches.map((m, i) => (
                    <SimilarityPlace
                      key={`m-${i}`}
                      severity={m.severity}
                      severityLabel={severityChipLabel(m.severity)}
                      text={m.text}
                      location={entryLocation(m)}
                      canGo={Number.isInteger(Number(m.section_index))}
                      goLabel={m.type === "section" || m.question_index == null ? "Go to section" : "Go to question"}
                      onGo={() => goToSimilarityEntry(m)}
                    />
                  ))}
                </Box>
              )}
              {(similarityDialog.conflicts || []).length > 0 && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {similarityDialog.conflicts.slice(0, 12).map((c, i) => (
                    <Box key={`c-${i}`}>
                      <SimilarityPlace
                        severity={c.severity}
                        severityLabel={severityChipLabel(c.severity)}
                        text={c.a?.text}
                        location={entryLocation(c.a)}
                        canGo={Number.isInteger(Number(c.a?.section_index))}
                        goLabel={c.a?.type === "section" || c.a?.question_index == null ? "Go to section" : "Go to question"}
                        onGo={() => goToSimilarityEntry(c.a)}
                      />
                      <SimilarityPlace
                        severity={c.severity}
                        severityLabel={severityChipLabel(c.severity)}
                        text={c.b?.text}
                        location={entryLocation(c.b)}
                        canGo={Number.isInteger(Number(c.b?.section_index))}
                        goLabel={c.b?.type === "section" || c.b?.question_index == null ? "Go to section" : "Go to question"}
                        onGo={() => goToSimilarityEntry(c.b)}
                        sx={{ mt: 1 }}
                      />
                    </Box>
                  ))}
                  {similarityDialog.conflicts.length > 12 && (
                    <Typography sx={metaSx}>
                      …and {similarityDialog.conflicts.length - 12} more
                    </Typography>
                  )}
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button
                variant="outlined"
                disableElevation
                disableRipple
                disableFocusRipple
                onClick={() => setSimilarityDialog(null)}
                sx={ghostBtnSx}
              >
                {similarityDialog.allowOverride === false ? "Close" : "Cancel"}
              </Button>
              {similarityDialog.allowOverride && similarityDialog.onProceed && (
                <Button
                  variant="contained"
                  disableElevation
                  disableRipple
                  disableFocusRipple
                  onClick={() => {
                    const proceed = similarityDialog.onProceed;
                    setSimilarityDialog(null);
                    proceed?.();
                  }}
                  sx={primaryBtnSx}
                >
                  {similarityDialog.proceedLabel || "Keep anyway"}
                </Button>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>

      {pageNotices}
    </Box>
  );
}
